import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Col } from 'reactstrap';
import CloseIcon from 'mdi-react/CloseIcon';
import GestureTapIcon from 'mdi-react/GestureTapIcon';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import renderField from '../../../shared/components/custom/Field';
import renderSelectField from '../../../shared/components/form/Select';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { partial } from '../../../util/translation/translation';
import {
  NOTIFY_EMAILS_MAX,
  OFFER_HOURS_MAX,
  bookingTotal,
  formatMoney,
  isValidEmail,
  krInputToOre,
  krInputValue,
  seriesNeedsSeriesPrice,
  vatOptions,
} from '../payments';

// The price and waitlist settings of an event or a series (EventsForm,
// SeriesForm). Amounts are typed in kronor; payments.js turns them into øre.

const FieldError = ({ meta }) =>
  (meta.touched || meta.submitFailed) && meta.error ? (
    <span className="form__form-group-error">{meta.error}</span>
  ) : null;

// The hint goes under the field, so fields side by side stay level
const FormGroup = ({ label, hint, children }) => (
  <div className="form__form-group">
    <span className="form-header">{label}</span>
    {children}
    {hint && <small className="event-payments__hint">{hint}</small>}
  </div>
);

FormGroup.propTypes = {
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  children: PropTypes.node.isRequired,
};

FormGroup.defaultProps = {
  hint: null,
};

// Up to five email addresses, added one at a time and shown as chips
const EmailsField = ({ input, meta }) => {
  const p = partial('EventPayments');
  const s = partial('shared');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const emails = Array.isArray(input.value) ? input.value : [];
  const full = emails.length >= NOTIFY_EMAILS_MAX;
  const add = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      setError(p('errorEmail'));
      return;
    }
    if (!emails.includes(email)) {
      input.onChange([...emails, email]);
    }
    setDraft('');
    setError('');
  };
  return (
    <div className="form__form-group-input-wrap">
      <div className="event-payments__email-add">
        <input
          type="email"
          value={draft}
          placeholder={full ? p('notifyEmailsFull') : s('email')}
          aria-label={p('notifyEmails')}
          disabled={full}
          onChange={(event) => {
            setDraft(event.target.value);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
          onBlur={() => draft.trim() && add()}
        />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0"
          onClick={add}
          disabled={!draft.trim() || full}
        >
          {p('addEmail')}
        </button>
      </div>
      {error && <span className="form__form-group-error">{error}</span>}
      {emails.length > 0 && (
        <div className="event-payments__chips">
          {emails.map((email) => (
            <span className="series-chip" key={email}>
              {email}
              <button
                type="button"
                className="series-chip__remove"
                aria-label={p('removeEmail', { email })}
                onClick={() =>
                  input.onChange(
                    emails.filter((existing) => existing !== email)
                  )
                }
              >
                <CloseIcon size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
      <FieldError meta={meta} />
    </div>
  );
};

EmailsField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({}).isRequired,
};

/**
 * Price per place, booking fee and VAT; for a series also the whole-series
 * price (when it can be booked as a whole). The fee and VAT only show once
 * there's a price.
 */
export const PriceFields = ({ values, currency, series }) => {
  const p = partial('EventPayments');
  const paid = (krInputToOre(values.price) || 0) > 0;
  const showSeriesPrice = series && seriesNeedsSeriesPrice(values);
  const exampleTotal = bookingTotal(
    2,
    krInputToOre(values.price),
    krInputToOre(values.bookingFee)
  );
  return (
    <>
      <div className="flex">
        <Col md={6}>
          <FormGroup
            label={p('pricePerPlace', { currency })}
            hint={p('pricePerPlaceHint')}
          >
            <Field
              name="price"
              component={renderField}
              placeholder={p('free')}
              inputMode="decimal"
            />
          </FormGroup>
        </Col>
        {paid && (
          <Col md={6}>
            <FormGroup
              label={p('bookingFee', { currency })}
              hint={p('bookingFeeHint')}
            >
              <Field
                name="bookingFee"
                component={renderField}
                placeholder="0"
                inputMode="decimal"
              />
            </FormGroup>
          </Col>
        )}
      </div>
      {paid && (
        <div className="flex">
          <Col md={6}>
            <FormGroup label={p('vat')} hint={p('vatHint')}>
              <Field
                name="vat"
                component={renderSelectField}
                options={vatOptions(currency)}
                placeholder={p('vat')}
              />
            </FormGroup>
          </Col>
          {showSeriesPrice && (
            <Col md={6}>
              <FormGroup
                label={p('seriesPrice', { currency })}
                hint={p('seriesPriceHint')}
              >
                <Field
                  name="seriesPrice"
                  component={renderField}
                  inputMode="decimal"
                />
              </FormGroup>
            </Col>
          )}
        </div>
      )}
      {paid && (
        <Col md={12}>
          <InfoBox className="mb-3">
            {p('priceExample', {
              price: formatMoney(krInputToOre(values.price), currency),
              fee: formatMoney(krInputToOre(values.bookingFee), currency),
              total: formatMoney(exampleTotal, currency),
            })}
          </InfoBox>
        </Col>
      )}
    </>
  );
};

PriceFields.propTypes = {
  values: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bookingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bookingMode: PropTypes.string,
  }).isRequired,
  currency: PropTypes.string,
  series: PropTypes.bool,
};

PriceFields.defaultProps = {
  currency: '',
  series: false,
};

/**
 * How freed places are given out (by the admin, or automatically to the
 * first in line), the time an offered person has to pay (paid events) and
 * who's told when a place frees up on a manual waitlist.
 */
export const WaitlistFields = ({ values }) => {
  const p = partial('EventPayments');
  const paid = krInputValue(values.price) > 0;
  const manual = values.waitlistMode !== 'automatic';
  return (
    <>
      <Col md={12}>
        <FormGroup label={p('waitlistMode')}>
          <Field
            name="waitlistMode"
            component={ChoiceCards}
            ariaLabel={p('waitlistMode')}
            columns={1}
            options={[
              {
                value: 'manual',
                icon: GestureTapIcon,
                label: p('waitlistMode-manual'),
                hint: p('waitlistModeHint-manual'),
              },
              {
                value: 'automatic',
                icon: AutorenewIcon,
                label: p('waitlistMode-automatic'),
                hint: paid
                  ? p('waitlistModeHint-automaticPaid')
                  : p('waitlistModeHint-automaticFree'),
              },
            ]}
          />
        </FormGroup>
      </Col>
      {paid && (
        <Col md={6}>
          <FormGroup
            label={p('offerHours')}
            hint={p('offerHoursHint', { max: OFFER_HOURS_MAX })}
          >
            <Field name="offerHours" component={renderField} type="number" />
          </FormGroup>
        </Col>
      )}
      {manual && (
        <Col md={12}>
          <FormGroup
            label={p('notifyEmails')}
            hint={p('notifyEmailsHint', { max: NOTIFY_EMAILS_MAX })}
          >
            <Field name="waitlistNotifyEmails" component={EmailsField} />
          </FormGroup>
        </Col>
      )}
    </>
  );
};

WaitlistFields.propTypes = {
  values: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    waitlistMode: PropTypes.string,
  }).isRequired,
};
